import { makeAutoObservable } from 'mobx'
import { isAxiosError } from 'axios'
import { logger } from 'shared/lib/logger'

type IPaginationTimeoutRetryConfig = {
  storeKey: string
  minLimit: number
  getLimit: () => number | null
  reduceLimit: (limit: number) => void
  retryLoadData: () => void
}

export class PaginationTimeoutRetry {
  private _hasRetriedAtMinLimit = false
  private _isRetrying = false

  constructor(private _config: IPaginationTimeoutRetryConfig) {
    makeAutoObservable(this)
  }

  get isRetrying() {
    return this._isRetrying
  }

  handleSuccess = () => {
    this._hasRetriedAtMinLimit = false
    this._isRetrying = false
  }

  handleError = (error: unknown): boolean => {
    const isTimeout =
      isAxiosError(error) && (error.response?.status === 408 || error.response?.status === 504)

    if (!isTimeout) {
      this._hasRetriedAtMinLimit = false
      this._isRetrying = false
      return false
    }

    logger.warn(
      `TABLE: ${this._config.storeKey} Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.`
    )

    const currentLimit = this._config.getLimit()

    if (currentLimit === null || currentLimit > this._config.minLimit) {
      this._hasRetriedAtMinLimit = false
      this._isRetrying = true
      this._config.reduceLimit(this._config.minLimit)

      return true
    }

    if (!this._hasRetriedAtMinLimit) {
      this._hasRetriedAtMinLimit = true
      this._isRetrying = true
      this._config.retryLoadData()

      return true
    }

    this._hasRetriedAtMinLimit = false
    this._isRetrying = false

    return false
  }
}
